import { setOfflineOwner } from './offlineStore';
import { clearAuthSession } from '@/lib/apiConfig';
import React, { createContext, useState, useContext, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { api } from '@/api/apiClient';
import { msalService } from '@/lib/msalService';
import { unregisterPush } from '@/lib/nativePush';
import { disableWebPush } from '@/lib/webPush';

const AuthContext = createContext();

function storeSessionToken(authResponse) {
  const { session_token: sessionToken, ...currentUser } = authResponse;
  if (sessionToken) {
    localStorage.setItem('token', sessionToken);
    api.token = sessionToken;
  }
  return currentUser;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState({});
  const [impersonating, setImpersonating] = useState(false);
  const [impersonatorName, setImpersonatorName] = useState(null);

  useEffect(() => {
    checkAppState();
  }, []);

  const checkAppState = async () => {
    try {
      setIsLoadingAuth(true);
      setAuthError(null);

      const token = localStorage.getItem('token');
      if (token) {
        await checkUserAuth();
      } else {
        setIsLoadingAuth(false);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('App state check failed:', error);
      setAuthError({
        type: 'unknown',
        message: error.message || 'An unexpected error occurred'
      });
      setIsLoadingAuth(false);
    }
  };

  const checkUserAuth = async () => {
    try {
      setIsLoadingAuth(true);
      const currentUser = storeSessionToken(await api.auth.me());
      await setOfflineOwner(currentUser).catch(() => {});
      setUser(currentUser);
      setIsAuthenticated(true);
      Sentry.setUser({ id: currentUser._id, email: currentUser.email });
      sessionStorage.removeItem('msal_auto_exchange_attempted');
      setImpersonating(!!currentUser.impersonating);
      setImpersonatorName(currentUser.impersonator_name || null);
      setIsLoadingAuth(false);
    } catch (error) {
      if (error.networkError && localStorage.getItem('token')) {
        const eventId = window.location.pathname.match(/^\/event\/([^/]+)/)?.[1];
        window.location.replace(window.location.pathname === '/uniform-requests' ? '/src/uniform/offline.html' : window.location.pathname === '/faulty-equipment' ? '/src/equipment/offline.html' : eventId ? `/offline.html?event=${encodeURIComponent(eventId)}` : window.location.pathname.startsWith('/documentation') ? `/offline.html?docs=1&${window.location.search.slice(1)}` : '/offline.html');
        return;
      }
      console.error('User auth check failed:', error);
      setIsLoadingAuth(false);
      setIsAuthenticated(false);
      if (error.status !== 401) {
        setAuthError({ type: 'auth_unavailable', message: 'We could not check your session. Your sign-in has been kept. Please try again.' });
        return;
      }
      clearAuthSession();
      sessionStorage.setItem('explicit_logout', '1');
      setAuthError({
        type: 'auth_required',
        message: 'Authentication required'
      });
    }
  };

  const logout = async (shouldRedirect = true) => {
    await disableWebPush().catch(() => {});
    if (!user?.approval_status || user.approval_status === 'accepted') await unregisterPush().catch(() => {});
    setUser(null);
    setIsAuthenticated(false);
    setImpersonating(false);
    setImpersonatorName(null);
    Sentry.setUser(null);
    clearAuthSession();
    api.token = null;
    localStorage.removeItem('impersonate_user_id');
    sessionStorage.setItem('explicit_logout', '1');
    await msalService.logout();

    if (shouldRedirect) {
      window.location.href = '/login';
    }
  };

  const startImpersonating = async (userId) => {
    localStorage.setItem('impersonate_user_id', userId);
    await checkUserAuth();
  };

  const stopImpersonating = async () => {
    localStorage.removeItem('impersonate_user_id');
    setImpersonating(false);
    setImpersonatorName(null);
    await checkUserAuth();
  };

  const refreshUser = async () => {
    try {
      const currentUser = storeSessionToken(await api.auth.me());
      await setOfflineOwner(currentUser).catch(() => {});
      setUser(currentUser);
      return currentUser;
    } catch {}
  };

  const navigateToLogin = () => {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `/login?returnUrl=${returnUrl}`;
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      logout,
      navigateToLogin,
      checkAppState,
      refreshUser,
      setBrandingPreference: (id, theme) => setUser(current => (current?.id || current?._id) === id ? { ...current, branding_theme: theme } : current),
      setColorSchemePreference: (id, scheme) => setUser(current => (current?.id || current?._id) === id ? { ...current, color_scheme: scheme } : current),
      impersonating,
      impersonatorName,
      startImpersonating,
      stopImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
